/**
 * «Revisar el dibujo» (DWG/DXF): lista de avisos del diagnóstico (shared/diagnosticoDelDibujo.ts). Cada aviso dice qué
 * pasa, por qué afecta el presupuesto y qué hacer, y puede señalarse en el plano. Solo avisa: no cambia cantidades.
 */
import { useState } from 'react';
import { Button, Spinner, makeStyles } from '@fluentui/react-components';
import { ChevronDown16Regular, ChevronRight16Regular, DismissCircle16Filled, Info16Regular, Warning16Filled } from '@fluentui/react-icons';
import type { AvisoDelDibujo, DiagnosticoDelDibujo } from '@supernova/shared';

const useStyles = makeStyles({
  raiz: { display: 'flex', flexDirection: 'column', gap: '6px' },
  titulo: { margin: 0, fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' },
  lista: { listStyleType: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '4px' },
  fila: { display: 'flex', alignItems: 'flex-start', gap: '6px', fontSize: '13px', lineHeight: 1.4, color: 'var(--text-primary)' },
  cabeza: {
    display: 'flex', alignItems: 'flex-start', gap: '6px', flex: 1, background: 'transparent', border: 'none', padding: 0,
    textAlign: 'left', cursor: 'pointer', color: 'inherit', font: 'inherit',
  },
  detalle: { margin: '2px 0 4px 22px', display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.4 },
  toca: { fontSize: '12px', color: 'var(--text-tertiary)' },
  nota: { margin: 0, fontSize: '12px', color: 'var(--text-tertiary)', lineHeight: 1.4 },
});

const ICONO = {
  alta: <DismissCircle16Filled style={{ color: 'var(--bad)' }} />,
  media: <Warning16Filled style={{ color: 'var(--warn)' }} />,
  info: <Info16Regular style={{ color: 'var(--text-tertiary)' }} />,
} as const;

export interface DiagnosticoDelDibujoProps {
  diagnostico: DiagnosticoDelDibujo | null;
  leyendo: boolean;
  /** Señala el aviso en el plano; sin esto no se ofrece el botón. */
  onVer?: (a: AvisoDelDibujo) => void;
  /** Aviso ya señalado (para ofrecer «Quitar resaltado»). */
  verActivo?: boolean;
  onQuitarResaltado?: () => void;
  /** Para «Revisar el modelo»: el título, el texto del botón y lo que se dice si no hay avisos. Por omisión, los del dibujo. */
  titulo?: string;
  textoVer?: string;
  textoSinAvisos?: string;
}

export function DiagnosticoDelDibujoLista({
  diagnostico,
  leyendo,
  onVer,
  verActivo,
  onQuitarResaltado,
  titulo = 'Revisar el dibujo',
  textoVer = 'Ver en el plano',
  textoSinAvisos = 'No encontramos piezas repetidas, trazos sueltos ni medidas que no cuadren.',
}: DiagnosticoDelDibujoProps) {
  const s = useStyles();
  const [abiertos, setAbiertos] = useState<ReadonlySet<number>>(new Set());
  if (!diagnostico && !leyendo) return null;
  const alternar = (i: number) =>
    setAbiertos((a) => {
      const n = new Set(a);
      if (n.has(i)) n.delete(i);
      else n.add(i);
      return n;
    });
  return (
    <div className={s.raiz}>
      <p className={s.titulo}>{titulo}</p>
      {leyendo && <Spinner size="tiny" label="Revisando el dibujo…" labelPosition="after" />}
      {diagnostico && diagnostico.avisos.length === 0 && !leyendo && (
        <p className={s.nota}>{textoSinAvisos}</p>
      )}
      {diagnostico && diagnostico.avisos.length > 0 && (
        <ul className={s.lista}>
          {diagnostico.avisos.map((a, i) => {
            const abierto = abiertos.has(i);
            return (
              <li key={`${a.clave}-${i}`}>
                <div className={s.fila}>
                  <button type="button" className={s.cabeza} onClick={() => alternar(i)} aria-expanded={abierto}>
                    {abierto ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
                    {ICONO[a.gravedad]}
                    <span>
                      {a.titulo} <span className={s.toca}>· Toca {a.piezas.toLocaleString('es-MX')} {a.piezas === 1 ? 'pieza' : 'piezas'}</span>
                    </span>
                  </button>
                </div>
                {abierto && (
                  <div className={s.detalle}>
                    <span>{a.quePasa}</span>
                    <span>{a.porQueAfecta}</span>
                    <span>
                      <b>Qué hacer:</b> {a.queHacer}
                    </span>
                    {(a as { notas?: string[] }).notas?.map((n) => (
                      <span key={n}>{n}</span>
                    ))}
                    {onVer && a.handles.length > 0 && (
                      <span>
                        <Button size="small" appearance="primary" onClick={() => onVer(a)}>
                          {textoVer}
                        </Button>
                      </span>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {verActivo && onQuitarResaltado && (
        <span>
          <Button size="small" appearance="subtle" onClick={onQuitarResaltado}>
            Quitar resaltado
          </Button>
        </span>
      )}
      {diagnostico?.parcial && !leyendo && (
        <p className={s.nota}>Abre el plano en el visor para revisar también sus piezas.</p>
      )}
    </div>
  );
}
